import * as React from 'react';

type UseDropdownBlockStateParams = {
  /** The block id; when it disappears the block is treated as removed. */
  blockId: string | undefined;
  /** The block's current `value` prop. */
  blockValue: string | undefined;
  /**
   * The block's `props` object reference. A change in identity (not just
   * `blockValue`) signals an external update — e.g. after save/reload — and
   * forces the local value to re-sync.
   */
  blockProps: unknown;
};

type UseDropdownBlockStateResult = {
  /** True once the block has deferred past mount and is safe to render fully. */
  isReady: boolean;
  /** True when the block lost its id (is being removed). */
  isRemoved: boolean;
  /** Locally tracked selected value, kept in sync with the block prop. */
  currentValue: string;
  setCurrentValue: (value: string) => void;
};

/**
 * Owns the dropdown block's defer-until-mounted state machine: it holds the
 * mount-defer flag, removal detection, and the locally-tracked value that
 * re-syncs when the block updates externally (e.g. after save/reload).
 *
 * Extracted from the renderer so the presentation stays declarative; all hooks
 * run unconditionally and in a stable order.
 */
export function useDropdownBlockState({
  blockId,
  blockValue,
  blockProps,
}: UseDropdownBlockStateParams): UseDropdownBlockStateResult {
  const [isReady, setIsReady] = React.useState(false);
  const [isRemoved, setIsRemoved] = React.useState(false);

  // Track the entire props object reference to detect updates after save
  const blockPropsRef = React.useRef(blockProps);

  // Track value changes to force re-render when dropdown value updates
  const [currentValue, setCurrentValue] = React.useState(blockValue || '');

  React.useEffect(() => {
    // Defer rendering until after the component has mounted
    // This ensures BlockNote has finished position resolution
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  // Update local state when block value changes (from external updates like save)
  // Track both blockValue and props reference to detect updates after save
  React.useEffect(() => {
    const newValue = blockValue || '';
    const currentProps = blockProps;

    // Check if props object reference changed (indicates block was updated)
    const propsChanged = currentProps !== blockPropsRef.current;

    // Update if value changed OR props reference changed (after save/reload)
    if (newValue !== currentValue || propsChanged) {
      setCurrentValue(newValue);
      blockPropsRef.current = currentProps;
    }
  }, [blockValue, blockId, currentValue]);

  // Monitor block removal - if block becomes undefined or loses its id, mark as removed
  React.useEffect(() => {
    if (!blockId) {
      setIsRemoved(true);
    } else {
      setIsRemoved(false);
    }
  }, [blockId]);

  return { isReady, isRemoved, currentValue, setCurrentValue };
}
